import { DependencyList, useRef } from 'react';

/**
 * A version of `useMemo` that guarantees memoization.
 * See https://reactjs.org/docs/hooks-reference.html#usememo for an explanation why `useMemo` itself
 * does not have the same guarantees.
 *
 * @param factory function to create the desired value
 * @param deps list of dependencies
 * @returns {*} the result of `factory` or the previous value if the dependencies have not changed
 */
export const useMemoGuaranteed = <T>(factory: (previous?: T) => T, deps: DependencyList): T => {
    const prevDepsRef = useRef<DependencyList | undefined>(undefined);
    const prevValueRef = useRef<T | undefined>(undefined);
    if (
        !prevDepsRef.current ||
        prevDepsRef.current.length !== deps.length ||
        !prevDepsRef.current.every((prevDep, i) => prevDep === deps[i])
    ) {
        prevValueRef.current = factory(prevValueRef.current);
        prevDepsRef.current = deps;
    }

    return prevValueRef.current!; // eslint-disable-line @typescript-eslint/no-non-null-assertion
};
